import { errorApiRef, useApi, useRouteRef } from "@backstage/core-plugin-api";
import {
	scaffolderApiRef,
	SecretsContextProvider,
	useTemplateSecrets,
} from "@backstage/plugin-scaffolder-react";
import { Workflow, type WorkflowProps } from "@backstage/plugin-scaffolder-react/alpha";
import scaffolderPlugin from "@backstage/plugin-scaffolder/alpha";
import { ButtonLink } from "@backstage/ui";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { PageHero, PageShell, StateCard } from "./pageShell";

const useStyles = makeStyles({
	// Workflow wraps its card in core-components' <Content>, which pads on top of the shell's own gutter.
	form: { "& > article": { padding: 0 } },
});

// A scaffolder template's form in the page shell, instead of the stock /create wizard.
// What it runs and leaves out: docs/app/frontend-system.md § Template forms in the shell.

type TemplateFormProps = Readonly<{
	eyebrow: string;
	title: string;
	templateName: string;
	back: { href: string; label: string };
}>;

function TemplateForm(props: TemplateFormProps): ReactNode {
	const { eyebrow, title, templateName, back } = props;
	const classes = useStyles();
	const scaffolder = useApi(scaffolderApiRef);
	const errorApi = useApi(errorApiRef);
	const { secrets } = useTemplateSecrets();
	const navigate = useNavigate();
	const taskRoute = useRouteRef(scaffolderPlugin.routes.ongoingTask);
	// A second Create while one is in flight would dispatch the workflow twice.
	const creating = useRef(false);

	const onCreate = useCallback(
		async (values: Parameters<WorkflowProps["onCreate"]>[0]) => {
			if (creating.current) return;
			creating.current = true;
			try {
				const { taskId } = await scaffolder.scaffold({
					templateRef: `template:default/${templateName}`,
					values,
					secrets,
				});
				navigate(taskRoute ? taskRoute({ taskId }) : `/create/tasks/${taskId}`);
			} catch (error) {
				creating.current = false;
				errorApi.post(error instanceof Error ? error : new Error(String(error)));
			}
		},
		[scaffolder, errorApi, secrets, navigate, taskRoute, templateName],
	);

	// The template was not served: the policy hides the gated ones from a colleague without the group.
	const onError = useCallback(
		() => (
			<StateCard
				role="alert"
				title="This form is not available"
				action={<ButtonLink href={back.href}>{back.label}</ButtonLink>}
			>
				It could not be loaded for your account.
			</StateCard>
		),
		[back.href, back.label],
	);

	return (
		<PageShell>
			<PageHero
				eyebrow={eyebrow}
				title={title}
				meta={
					<ButtonLink variant="secondary" href={back.href}>
						{back.label}
					</ButtonLink>
				}
			/>
			<div className={classes.form}>
				<Workflow
					namespace="default"
					templateName={templateName}
					extensions={[]}
					onCreate={onCreate}
					onError={onError}
				/>
			</div>
		</PageShell>
	);
}

/** The template named `templateName`, as a page: hero, back link, then the form from the template's YAML. */
export function TemplateFormPage(props: TemplateFormProps): ReactNode {
	return (
		<SecretsContextProvider>
			<TemplateForm {...props} />
		</SecretsContextProvider>
	);
}
